import React from 'react';

import { AskAiRule } from '@lumapps/ask-ai-rules/types';
import { background, margin, padding, typography, useClassnames } from '@lumapps/classnames';
import { Alignment, ColorPalette, ColorVariant, FlexBox, Orientation, Size, Text } from '@lumapps/lumx/react';
import { useTranslate } from '@lumapps/translations';
import { MarkdownContentView } from '@lumapps/wrex-markdown/components/MarkdownContentView';

import { trackAskClick } from '../../api/analytics';
import { ASK_AI } from '../../keys';
import { ACTIONABLE_ITEM_TYPES, AskAiDocumentTypes, AskAiSource, AskSearchBestAnswer } from '../../types';
import { getAskAiSourceTitle, isActionableItemValid } from '../../utils';
import { AskAiAnswerFooter } from '../AskAiAnswerFooter';
import { AskAiCard } from '../AskAiCard';
import { AskAiSourcesList } from '../AskAiSourcesList';

import './index.scss';

export interface AskAiAnswerProps {
    className?: string;
    answer?: AskSearchBestAnswer;
    /** Unique ID to identify each search */
    traceId: string;
    /** Whether the component is used in the micro-apps builder context, only for preview */
    isPreview?: boolean;
    /** The query from which the answer has been generated. */
    query: string;
    /** Whether the answer is currently being streamed */
    isStreaming?: boolean;
    /** Should we display the agent name or not */
    shouldDisplayAgentName: boolean;
    /** Should we display reactions and agent name or not */
    shouldDisplayFooter?: boolean;
    /** Handle change agentId */
    handleAgentChange?: (rule: AskAiRule) => void;
}

const CLASSNAME = 'ask-ai-answer';

/**
 * Display the answer given by the Ask AI api with some extra design
 */
export const AskAiAnswer: React.FC<AskAiAnswerProps> = ({
    className,
    answer,
    traceId,
    isPreview = false,
    query,
    isStreaming,
    shouldDisplayAgentName = false,
    shouldDisplayFooter = true,
    handleAgentChange,
}) => {
    const { block, element } = useClassnames(CLASSNAME);
    const { translateKey } = useTranslate();

    if (!answer) {
        return null;
    }

    const hasValidActionableItem = answer.actionableItem && isActionableItemValid(answer.actionableItem);
    const hasValidMicroApp = hasValidActionableItem && answer.actionableItem?.type === ACTIONABLE_ITEM_TYPES.MICRO_APP;

    // remove from source list all potential directory entries with no link to avoid having chips without interactions
    const filteredSources = answer?.sources?.filter((source) =>
        source.type === AskAiDocumentTypes.DIRECTORY_ENTRY ? Boolean(source.url) : true,
    );

    /**
     * Display source if some are left after filtering.
     */
    const hasSources = filteredSources && filteredSources?.length > 0;

    /**
     * Track a click on a source, but only in the Ask AI context: not in preview (micro-apps builder /
     * bulk) and only when we have a traceId and query to attach the event to
     */
    const handleSourceClick =
        !isPreview && traceId && query
            ? (source: AskAiSource) =>
                  trackAskClick({
                      traceId,
                      query,
                      targetId: source.id,
                      targetType: source.type,
                      // Raw URL (no site base slug)
                      url: source.url,
                      // Track the displayed title, falling back to the default title for untitled sources
                      title: getAskAiSourceTitle(source, translateKey),
                  })
            : undefined;

    const actionableItemWrapped = (
        <FlexBox
            orientation={Orientation.horizontal}
            className={element('actionable-item', [
                background(ColorPalette.primary, ColorVariant.L6),
                padding('all', Size.big),
                margin('all', Size.big),
            ])}
            vAlign={Alignment.center}
        >
            <AskAiCard
                className={element('actionable-item-card')}
                actionableItem={answer.actionableItem}
                query={query}
            />
        </FlexBox>
    );

    return (
        <FlexBox className={block(undefined, className)} orientation="vertical" gap={Size.regular}>
            <FlexBox orientation="vertical" gap={Size.medium}>
                {hasValidMicroApp && (
                    <>
                        <Text as="p" className={margin('bottom', Size.big)}>
                            {translateKey(ASK_AI.MICRO_APP_ANSWER_INTRODUCTION)}
                        </Text>
                        {actionableItemWrapped}
                    </>
                )}
                <div className={element('wrapper', [typography('body1')])}>
                    <MarkdownContentView markdown={answer.answer} contentViewProps={{ isStreaming }} />
                </div>

                {hasValidActionableItem && !hasValidMicroApp && actionableItemWrapped}
            </FlexBox>

            {hasSources && <AskAiSourcesList sources={filteredSources} onSourceClick={handleSourceClick} />}
            {!isStreaming && shouldDisplayFooter && (
                <AskAiAnswerFooter
                    answer={answer}
                    traceId={traceId}
                    query={query}
                    isPreview={isPreview}
                    isAnimated
                    shouldDisplayAgentName={shouldDisplayAgentName}
                    size="m"
                    handleAgentChange={handleAgentChange}
                    isStreaming={isStreaming}
                />
            )}
        </FlexBox>
    );
};
